import { useEffect } from "react";

type GuardedTextFieldWrapper = HTMLElement & {
    __kefiDragPropagationGuardAttached?: boolean;
};

const TEXT_FIELD_SELECTOR = ".Polaris-TextField";
const NUMBER_INPUT_SELECTOR = 'input[type="number"]';
const GUARDED_EVENTS: Array<keyof GlobalEventHandlersEventMap> = ["mousedown", "pointerdown", "touchstart"];

const stopPropagation = (event: Event) => {
    event.stopPropagation();
};

const shouldGuardWrapper = (wrapper: Element) => {
    return !!wrapper.querySelector(NUMBER_INPUT_SELECTOR);
};

const attachGuard = (wrapper: Element) => {
    const guardedWrapper = wrapper as GuardedTextFieldWrapper;
    if (guardedWrapper.__kefiDragPropagationGuardAttached) return;
    if (!shouldGuardWrapper(guardedWrapper)) return;

    GUARDED_EVENTS.forEach((eventName) => {
        guardedWrapper.addEventListener(eventName, stopPropagation, true);
    });

    guardedWrapper.__kefiDragPropagationGuardAttached = true;
};

const detachGuard = (wrapper: Element) => {
    const guardedWrapper = wrapper as GuardedTextFieldWrapper;
    if (!guardedWrapper.__kefiDragPropagationGuardAttached) return;

    GUARDED_EVENTS.forEach((eventName) => {
        guardedWrapper.removeEventListener(eventName, stopPropagation, true);
    });

    guardedWrapper.__kefiDragPropagationGuardAttached = false;
};

const guardNode = (node: Node) => {
    if (!(node instanceof HTMLElement)) return;

    if (node.matches(TEXT_FIELD_SELECTOR)) {
        attachGuard(node);
    }

    node.querySelectorAll(TEXT_FIELD_SELECTOR).forEach((wrapper) => {
        attachGuard(wrapper);
    });
};

export default function useNumberInputDragPropagationGuard() {
    useEffect(() => {
        document.querySelectorAll(TEXT_FIELD_SELECTOR).forEach((wrapper) => {
            attachGuard(wrapper);
        });

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((addedNode) => {
                    guardNode(addedNode);
                });
            });
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });

        return () => {
            observer.disconnect();
            document.querySelectorAll(TEXT_FIELD_SELECTOR).forEach((wrapper) => {
                detachGuard(wrapper);
            });
        };
    }, []);
}
